import { z } from 'zod';
import type { Action } from '../permissions.js';
import { Role } from './common.js';
import type { ModuleKey } from './modules.js';

/**
 * Vague 7 : **Accueil en widgets**, réduit le 02/10/2026 (décision du porteur) à **« Ma journée »** :
 * - en-tête fixe (« Bonjour <prénom> » et phrase de synthèse datée et cliquable), hors catalogue ;
 * - `todo` : « À faire maintenant », une seule liste triée par urgence (règle `attentionList`) ;
 * - `today` et `month` : deux petits blocs côte à côte (empilés au téléphone), « Aujourd'hui » et
 *   « Ce mois-ci ».
 * Chaque membre peut masquer ou réordonner ces blocs ; à défaut, la disposition par défaut du garage pour son
 * rôle (réglée par le propriétaire), sinon celle du produit. Les données de chaque bloc passent par les
 * routes existantes (`HOME_WIDGETS[*].sources`), jamais par une route géante. Un bloc dont le membre n'a pas
 * le droit, le rôle ou le module est **ignoré côté serveur** à la lecture (la disposition enregistrée est
 * gardée telle quelle). Les anciennes clés (avant le 02/10/2026) sont relues par `normalizeHomeLayout`.
 */

export const HOME_WIDGET_KEYS = ['todo', 'today', 'month'] as const;
export const HomeWidgetKey = z.enum(HOME_WIDGET_KEYS);
export type HomeWidgetKey = z.infer<typeof HomeWidgetKey>;

/** `m` : une demie (côte à côte sur ordinateur), `l` : pleine largeur. Sur téléphone, tout est pleine largeur. */
export const HomeWidgetSize = z.enum(['s', 'm', 'l']);
export type HomeWidgetSize = z.infer<typeof HomeWidgetSize>;

export interface HomeWidgetDefinition {
  label: string;
  description: string;
  sizes: readonly HomeWidgetSize[];
  defaultSize: HomeWidgetSize;
  /** Droits **tous** requis (en plus de `intervention.view`, exigé par l'Accueil). */
  permissions: readonly Action[];
  /** Rôles permis (absent : tous). Une session sans rôle (support) n'y a pas accès. */
  roles?: readonly Role[];
  /** Module requis (le noyau `dossiers` est toujours actif). */
  module: ModuleKey;
  /** Routes qui alimentent le widget (aucune route géante ; montants seulement avec `amounts.view`). */
  sources: readonly string[];
}

export const HOME_WIDGETS: Record<HomeWidgetKey, HomeWidgetDefinition> = {
  todo: {
    label: 'À faire maintenant',
    description:
      'Une seule liste triée par urgence : décisions reçues, questions des clients, véhicules à démarrer, en retard, gardiennage dépassé, sans action depuis quelques jours.',
    sizes: ['l'],
    defaultSize: 'l',
    permissions: [],
    module: 'dossiers',
    sources: ['interventions.list?scope=present&sort=priority', 'inbox.list (non-lus)'],
  },
  today: {
    label: 'Aujourd’hui',
    description: 'Les arrivées, mises à disposition et créneaux du jour, heure par heure.',
    sizes: ['m', 'l'],
    defaultSize: 'm',
    permissions: [],
    module: 'dossiers',
    sources: ['home.get (plannedArrivals.today, interventions)'],
  },
  month: {
    label: 'Ce mois-ci',
    description: 'Travaux complémentaires acceptés, taux d’accord et délai médian de décision ; le détail dans « Chiffres ».',
    sizes: ['m', 'l'],
    defaultSize: 'm',
    permissions: ['amounts.view'],
    roles: ['owner', 'partner'],
    module: 'atelier_metrics',
    sources: ['metrics.atelier'],
  },
};

export const HomeLayoutItem = z.strictObject({ widget: HomeWidgetKey, size: HomeWidgetSize });
export type HomeLayoutItem = z.infer<typeof HomeLayoutItem>;

/** Liste ordonnée, chaque widget au plus une fois, dans une taille permise. */
export const HomeLayout = z
  .array(HomeLayoutItem)
  .max(HOME_WIDGET_KEYS.length)
  .superRefine((items, ctx) => {
    const seen = new Set<string>();
    items.forEach((item, index) => {
      if (seen.has(item.widget)) ctx.addIssue({ code: 'custom', path: [index, 'widget'], message: 'Widget en double' });
      seen.add(item.widget);
      if (!HOME_WIDGETS[item.widget].sizes.includes(item.size)) {
        ctx.addIssue({ code: 'custom', path: [index, 'size'], message: 'Taille non permise pour ce widget' });
      }
    });
  });
export type HomeLayout = z.infer<typeof HomeLayout>;

const L = (widget: HomeWidgetKey, size: HomeWidgetSize): HomeLayoutItem => ({ widget, size });

/**
 * Disposition du produit, par rôle (dernier recours), 02/10/2026 : propriétaire et associé : À faire,
 * Aujourd'hui, Ce mois-ci ; accueil et mécanicien (« À faire » limité à ses dossiers) : À faire,
 * Aujourd'hui ; lecture seule : Aujourd'hui. La phrase de synthèse est toujours dans l'en-tête.
 */
export const PRODUCT_HOME_LAYOUTS: Record<Role, HomeLayout> = {
  owner: [L('todo', 'l'), L('today', 'm'), L('month', 'm')],
  partner: [L('todo', 'l'), L('today', 'm'), L('month', 'm')],
  frontdesk: [L('todo', 'l'), L('today', 'm')],
  mechanic: [L('todo', 'l'), L('today', 'm')],
  readonly: [L('today', 'm')],
};

/** Dispositions par défaut réglées par le propriétaire (`garage_settings.home_layout_defaults`). */
export const HomeLayoutDefaults = z.partialRecord(Role, HomeLayout);
export type HomeLayoutDefaults = z.infer<typeof HomeLayoutDefaults>;

// ─── Anciennes dispositions (avant le 02/10/2026) ───────────────────────────

/**
 * Anciennes clés (Accueil en 10 widgets) → bloc qui reprend leur rôle ; `null` : retiré de l'Accueil
 * (Compteurs → phrase de synthèse de l'en-tête ; Messagerie en direct → compteur du menu Messagerie).
 * Utilisé à la lecture des dispositions enregistrées : aucune migration SQL.
 */
export const LEGACY_HOME_WIDGETS: Readonly<Record<string, HomeWidgetKey | null>> = {
  counters: null,
  inbox_live: null,
  parc_priority: 'todo',
  decisions: 'todo',
  tasks: 'todo',
  storage: 'todo',
  arrivals: 'today',
  today_timeline: 'today',
  agenda: 'today',
  atelier_figures: 'month',
};

/**
 * Relit une disposition enregistrée avec indulgence : clés actuelles gardées, anciennes clés converties
 * (`LEGACY_HOME_WIDGETS`), clés inconnues et doublons ignorés, taille non permise remplacée par la taille
 * par défaut. `null` (disposition par défaut) si ce n'est pas une liste, ou si une liste non vide ne donne
 * plus aucun bloc valide ; une liste vide enregistrée reste vide (tout masqué : choix du membre).
 */
export function normalizeHomeLayout(raw: unknown): HomeLayout | null {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<HomeWidgetKey>();
  const out: HomeLayout = [];
  for (const entry of raw as unknown[]) {
    const key = (entry as { widget?: unknown } | null)?.widget;
    const size = (entry as { size?: unknown } | null)?.size;
    if (typeof key !== 'string') continue;
    const widget: HomeWidgetKey | null = (HOME_WIDGET_KEYS as readonly string[]).includes(key)
      ? (key as HomeWidgetKey)
      : (LEGACY_HOME_WIDGETS[key] ?? null);
    if (!widget || seen.has(widget)) continue;
    seen.add(widget);
    const def = HOME_WIDGETS[widget];
    out.push({
      widget,
      size: typeof size === 'string' && (def.sizes as readonly string[]).includes(size) ? (size as HomeWidgetSize) : def.defaultSize,
    });
  }
  return raw.length > 0 && out.length === 0 ? null : out;
}

/** Dispositions par défaut du garage relues avec la même indulgence (rôle inconnu ou liste invalide ignorés). */
export function normalizeHomeDefaults(raw: unknown): HomeLayoutDefaults {
  const out: HomeLayoutDefaults = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [role, layout] of Object.entries(raw as Record<string, unknown>)) {
    const r = Role.safeParse(role);
    if (!r.success) continue;
    const clean = normalizeHomeLayout(layout);
    if (clean) out[r.data] = clean;
  }
  return out;
}

export const HomeLayoutSource = z.enum(['member', 'garage', 'product']);

export interface HomeLayoutContext {
  role: Role | null;
  can: (action: Action) => boolean;
  modules: ReadonlySet<ModuleKey>;
}

/** Widget utilisable par ce membre dans ce garage (droits, rôle et module). */
export function homeWidgetAvailable(widget: HomeWidgetKey, ctx: HomeLayoutContext): boolean {
  const def = HOME_WIDGETS[widget];
  if (def.roles && !(ctx.role && def.roles.includes(ctx.role))) return false;
  return ctx.modules.has(def.module) && def.permissions.every((a) => ctx.can(a));
}

/**
 * Disposition effective : celle du membre, sinon celle du garage pour son rôle, sinon celle du produit ;
 * chacune relue par `normalizeHomeLayout` (anciennes clés converties ; plus rien de valide : niveau suivant),
 * puis les widgets sans droit, hors rôle ou dont le module est désactivé sont **ignorés**. Une session sans
 * rôle (support) prend la disposition du produit pour `readonly`.
 */
export function resolveHomeLayout(
  member: unknown,
  garageDefaults: HomeLayoutDefaults | null | undefined,
  ctx: HomeLayoutContext,
): { layout: HomeLayout; source: z.infer<typeof HomeLayoutSource> } {
  const role = ctx.role ?? 'readonly';
  const mine = normalizeHomeLayout(member);
  const fromGarage = ctx.role ? normalizeHomeLayout(garageDefaults?.[ctx.role]) : null;
  const [base, source] = mine
    ? ([mine, 'member'] as const)
    : fromGarage
      ? ([fromGarage, 'garage'] as const)
      : ([PRODUCT_HOME_LAYOUTS[role], 'product'] as const);
  return { layout: base.filter((item) => homeWidgetAvailable(item.widget, ctx)), source };
}

export const HomeWidgetOutput = z.object({
  widget: HomeWidgetKey,
  label: z.string(),
  description: z.string(),
  sizes: z.array(HomeWidgetSize),
  defaultSize: HomeWidgetSize,
  /** Le membre peut l'ajouter (droits, rôle et module) ; sinon il n'apparaît pas dans la disposition effective. */
  available: z.boolean(),
});

export const HomeLayoutOutput = z.object({
  layout: HomeLayout,
  source: HomeLayoutSource,
  catalog: z.array(HomeWidgetOutput),
});
export type HomeLayoutOutput = z.infer<typeof HomeLayoutOutput>;

/** `null` : revenir à la disposition par défaut (garage, sinon produit). */
export const HomeLayoutUpdateInput = z.strictObject({ layout: HomeLayout.nullable() });

/** Disposition par défaut d'un rôle (`null` : celle du produit). */
export const HomeDefaultsUpdateInput = z.strictObject({ role: Role, layout: HomeLayout.nullable() });

export const HomeDefaultsOutput = z.object({
  /** Dispositions réglées par le garage (un rôle absent suit celle du produit). */
  defaults: HomeLayoutDefaults,
  /** Dispositions du produit, pour « Revenir à la disposition du produit ». */
  product: z.record(Role, HomeLayout),
});
export type HomeDefaultsOutput = z.infer<typeof HomeDefaultsOutput>;
